import { FC, useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useModal } from '@farmshare/ui-components';
import {
  ChoiceGroupQuestion,
  ExtrasSelectionQuestion,
  FreeSelectionQuestion,
  GlobalSpecQuestion,
  InfoSlideQuestion,
  Question,
  TrimPercentageQuestion,
  TrimRankingQuestion,
} from '../../../types';
import type { CutsheetCatalog } from '../../../types';
import { EnumCutsheetQuestionnaireQuestionsType } from 'lib/graphql';

import { ItemPickerBody } from './item-picker-body';
import { GlobalSpecContent } from './global-spec-content';
import { FreeSelectionContent } from './free-selection-content';
import { ChoiceGroupContent } from './choice-group-content';
import { TrimRankingContent } from './trim-ranking-content';
import { TrimPercentageContent } from './trim-percentage-content';
import { ExtrasSelectionContent } from './extras-selection-content';
import { InfoSlideContent } from './info-slide-content';

export interface QuestionCardProps {
  question: Question;
  catalog: CutsheetCatalog;
  onEdit: (question: Question) => void;
  onDelete: (id: string) => void;
  onAddItem?: (questionId: string, itemId: string) => void;
  /** Batch-add multiple items at once — used by the click-to-select modal */
  onAddItems?: (questionId: string, itemIds: string[]) => void;
  onRemoveItem?: (questionId: string, itemId: string) => void;
  onEditItem?: (itemId: string, itemType: 'cut' | 'trim' | 'extra') => void;
  onAddCutToOption?: (questionId: string, optionId: string) => void;
  onRemoveCutFromOption?: (
    questionId: string,
    optionId: string,
    cutId: string,
  ) => void;
  /** Called when the user clicks the "move up" arrow; undefined disables the button */
  onMoveUp?: () => void;
  /** Called when the user clicks the "move down" arrow; undefined disables the button */
  onMoveDown?: () => void;
  /**
   * The type of capability currently being dragged, or null when nothing is
   * being dragged. Used to tint dropzones green (compatible) or red
   * (incompatible) so users know where they can drop before releasing.
   */
  draggingType?: 'cut' | 'trim' | 'extra' | 'specification' | null;
  /**
   * Called when the user drag-reorders items inside a FreeSelection question.
   * Receives the question id and the new ordered list of item ids.
   */
  onReorderItems?: (questionId: string, newOrderIds: string[]) => void;
  /**
   * Called when the user drag-reorders cuts inside a ChoiceGroup option.
   * Receives the questionId, optionId, and the new ordered list of cut ids.
   */
  onReorderOptionCuts?: (
    questionId: string,
    optionId: string,
    newOrderIds: string[],
  ) => void;
}

// ---------------------------------------------------------------------------
// Helpers — colour + label lookups keyed by question type
// ---------------------------------------------------------------------------

type PaletteKey =
  | 'primary'
  | 'secondary'
  | 'warning'
  | 'info'
  | 'success'
  | 'tertiary';

/**
 * Maps question type → MUI palette key used for the card tint + type chip.
 * Returns null for InfoSlide (and unknown types) so they get no colour wash.
 * @param type
 */
const getTypeColorKey = (
  type: EnumCutsheetQuestionnaireQuestionsType,
): PaletteKey | null => {
  switch (type) {
    case EnumCutsheetQuestionnaireQuestionsType.GlobalSpec:
      return 'primary';
    case EnumCutsheetQuestionnaireQuestionsType.FreeSelection:
      return 'secondary';
    case EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup:
      return 'warning';
    case EnumCutsheetQuestionnaireQuestionsType.TrimRanking:
      return 'info';
    case EnumCutsheetQuestionnaireQuestionsType.TrimPercentage:
      return 'success';
    case EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection:
      return 'info';
    case EnumCutsheetQuestionnaireQuestionsType.InfoSlide:
      return 'tertiary';
    default:
      return null;
  }
};

const getTypeLabel = (type: EnumCutsheetQuestionnaireQuestionsType): string => {
  switch (type) {
    case EnumCutsheetQuestionnaireQuestionsType.GlobalSpec:
      return 'Specification';
    case EnumCutsheetQuestionnaireQuestionsType.FreeSelection:
      return 'Multi-Choice Cuts';
    case EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup:
      return 'Either/Or Cuts';
    case EnumCutsheetQuestionnaireQuestionsType.TrimRanking:
      return 'Trim by Weight';
    case EnumCutsheetQuestionnaireQuestionsType.TrimPercentage:
      return 'Trim by Percentage';
    case EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection:
      return 'Extras Selection';
    case EnumCutsheetQuestionnaireQuestionsType.InfoSlide:
      return 'Info Slide';
    default:
      return 'Unknown';
  }
};

/**
 * Card component for displaying a single questionnaire question.
 *
 * Handles:
 * - Card chrome (coloured border/background, type chip, edit/delete/reorder actions)
 * - The item-picker modal logic centralised here so each content sub-component
 *   stays focused on display only
 * - Delegates content rendering to the appropriate `*Content` component
 * @param root0
 * @param root0.question
 * @param root0.catalog
 * @param root0.onEdit
 * @param root0.onDelete
 * @param root0.onAddItem
 * @param root0.onAddItems
 * @param root0.onRemoveItem
 * @param root0.onEditItem
 * @param root0.onAddCutToOption
 * @param root0.onRemoveCutFromOption
 * @param root0.onMoveUp
 * @param root0.onMoveDown
 * @param root0.draggingType
 * @param root0.onReorderItems
 * @param root0.onReorderOptionCuts
 */
export const QuestionCard: FC<QuestionCardProps> = ({
  question,
  catalog,
  onEdit,
  onDelete,
  onAddItem = undefined,
  onAddItems = undefined,
  onRemoveItem = undefined,
  onEditItem = undefined,
  onAddCutToOption = undefined,
  onRemoveCutFromOption = undefined,
  onMoveUp = undefined,
  onMoveDown = undefined,
  draggingType = null,
  onReorderItems = undefined,
  onReorderOptionCuts = undefined,
}) => {
  const { info } = useModal();

  /**
   * null  = nothing being dragged
   * true  = dragged type is compatible with this question
   * false = dragged type is incompatible
   */
  const canAccept = useMemo((): boolean | null => {
    if (!draggingType) return null;
    switch (question.type) {
      case EnumCutsheetQuestionnaireQuestionsType.GlobalSpec:
        return draggingType === 'specification';
      case EnumCutsheetQuestionnaireQuestionsType.FreeSelection:
      case EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup:
        return draggingType === 'cut';
      case EnumCutsheetQuestionnaireQuestionsType.TrimRanking:
      case EnumCutsheetQuestionnaireQuestionsType.TrimPercentage:
        return draggingType === 'trim';
      case EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection:
        return draggingType === 'extra';
      default:
        return false;
    }
  }, [draggingType, question.type]);

  /**
   * Opens an item-picker modal for this question type.
   *
   * All catalog items are shown (not just un-added ones) — the picker itself
   * shows Add/Remove buttons and fires `onAddItem` / `onRemoveItem` immediately.
   * The modal only needs a Close button; no form submit is required.
   */
  const handleAddClick = () => {
    if (!onAddItem && !onAddItems) return;

    type PickerItem = {
      id: string;
      title?: string;
      description?: string;
      imageUrl?: string;
      groupLabel?: string;
    };

    let allItems: PickerItem[] = [];
    let currentSelectedIds: string[] = [];
    let title = 'Manage Items';
    let accentColor = 'primary.main';

    switch (question.type) {
      case EnumCutsheetQuestionnaireQuestionsType.FreeSelection: {
        const q = question as FreeSelectionQuestion;
        // Enrich cuts with their primal name as the group label
        allItems = catalog.cuts.map((cut) => ({
          ...cut,
          groupLabel: cut.groupId
            ? (catalog.primals.find((p) => p.id === cut.groupId)?.title ??
              'Other')
            : 'Other',
        }));
        currentSelectedIds = q.availableCutIds;
        title = 'Manage Cuts';
        accentColor = 'secondary.main';
        break;
      }
      case EnumCutsheetQuestionnaireQuestionsType.TrimRanking: {
        const q = question as TrimRankingQuestion;
        allItems = catalog.trim;
        currentSelectedIds = q.availableTrimIds;
        title = 'Manage Trims for Ranking';
        accentColor = 'info.main';
        break;
      }
      case EnumCutsheetQuestionnaireQuestionsType.TrimPercentage: {
        const q = question as TrimPercentageQuestion;
        allItems = catalog.trim;
        currentSelectedIds = q.availableTrimIds;
        title = 'Manage Trims for Percentages';
        accentColor = 'success.main';
        break;
      }
      case EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection: {
        const q = question as ExtrasSelectionQuestion;
        allItems = catalog.extras;
        currentSelectedIds = q.availableExtraIds;
        title = 'Manage Extras';
        accentColor = 'info.main';
        break;
      }
      case EnumCutsheetQuestionnaireQuestionsType.GlobalSpec: {
        const q = question as GlobalSpecQuestion;
        allItems = catalog.specifications;
        currentSelectedIds = q.specificationId ? [q.specificationId] : [];
        title = 'Select Specification';
        accentColor = 'primary.main';
        break;
      }
      default:
        return;
    }

    if (allItems.length === 0) return;

    info({
      type: 'info',
      title,
      okText: 'Close',
      footer: undefined,
      size: 'xs',
      body: (
        <ItemPickerBody
          items={allItems}
          selectedIds={currentSelectedIds}
          onAdd={(id) => onAddItem?.(question.id, id)}
          onRemove={(id) => onRemoveItem?.(question.id, id)}
          accentColor={accentColor}
          maxSelections={
            question.type === EnumCutsheetQuestionnaireQuestionsType.GlobalSpec
              ? 1
              : undefined
          }
        />
      ),
    });
  };

  // ---------------------------------------------------------------------------
  // Card chrome helpers
  // ---------------------------------------------------------------------------

  const colorKey = getTypeColorKey(
    question.type as EnumCutsheetQuestionnaireQuestionsType,
  );
  const typeLabel = getTypeLabel(
    question.type as EnumCutsheetQuestionnaireQuestionsType,
  );

  // ---------------------------------------------------------------------------
  // Content rendering — delegated to dedicated sub-components
  // ---------------------------------------------------------------------------

  const renderContent = () => {
    switch (question.type) {
      case EnumCutsheetQuestionnaireQuestionsType.GlobalSpec:
        return (
          <GlobalSpecContent
            question={question as GlobalSpecQuestion}
            catalog={catalog}
            canAccept={canAccept}
            draggingType={draggingType}
            onAddItems={onAddItems}
            onRemoveItem={onRemoveItem}
            onAddClick={handleAddClick}
          />
        );

      case EnumCutsheetQuestionnaireQuestionsType.FreeSelection:
        return (
          <FreeSelectionContent
            question={question as FreeSelectionQuestion}
            catalog={catalog}
            canAccept={canAccept}
            draggingType={draggingType}
            onAddItem={onAddItem}
            onRemoveItem={onRemoveItem}
            onEditItem={onEditItem}
            onReorderItems={onReorderItems}
            onAddClick={handleAddClick}
          />
        );

      case EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup:
        return (
          <ChoiceGroupContent
            question={question as ChoiceGroupQuestion}
            catalog={catalog}
            canAccept={canAccept}
            draggingType={draggingType}
            onAddCutToOption={onAddCutToOption}
            onRemoveCutFromOption={onRemoveCutFromOption}
            onReorderOptionCuts={onReorderOptionCuts}
          />
        );

      case EnumCutsheetQuestionnaireQuestionsType.TrimRanking:
        return (
          <TrimRankingContent
            question={question as TrimRankingQuestion}
            catalog={catalog}
            canAccept={canAccept}
            draggingType={draggingType}
            onAddItem={onAddItem}
            onRemoveItem={onRemoveItem}
            onEditItem={onEditItem}
            onReorderItems={onReorderItems}
            onAddClick={handleAddClick}
          />
        );

      case EnumCutsheetQuestionnaireQuestionsType.TrimPercentage:
        return (
          <TrimPercentageContent
            question={question as TrimPercentageQuestion}
            catalog={catalog}
            canAccept={canAccept}
            draggingType={draggingType}
            onAddItem={onAddItem}
            onRemoveItem={onRemoveItem}
            onEditItem={onEditItem}
            onReorderItems={onReorderItems}
            onAddClick={handleAddClick}
          />
        );

      case EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection:
        return (
          <ExtrasSelectionContent
            question={question as ExtrasSelectionQuestion}
            catalog={catalog}
            canAccept={canAccept}
            draggingType={draggingType}
            onAddItem={onAddItem}
            onRemoveItem={onRemoveItem}
            onEditItem={onEditItem}
            onReorderItems={onReorderItems}
            onAddClick={handleAddClick}
          />
        );

      case EnumCutsheetQuestionnaireQuestionsType.InfoSlide:
        return <InfoSlideContent question={question as InfoSlideQuestion} />;

      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderWidth: 2,
        borderStyle: 'solid',
        // Diagonal gradient: top-left corner carries a light colour wash that
        // fades to plain paper by the bottom-right.
        background: (theme) => {
          const key = colorKey as keyof typeof theme.palette;
          const { paper } = theme.palette.background;
          const paletteEntry = key
            ? (theme.palette[key] as { main?: string })
            : undefined;
          return paletteEntry?.main
            ? `linear-gradient(90deg, ${alpha(paletteEntry.main, 0.1)} 0%, ${alpha(paletteEntry.main, 0.05)} 100%)`
            : paper;
        },
        borderColor: (theme) => {
          const key = colorKey as keyof typeof theme.palette;
          const paletteEntry = key
            ? (theme.palette[key] as { light?: string })
            : undefined;
          return paletteEntry?.light ?? theme.palette.divider;
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, py: 0.5 }}>
        <CardHeader
          title={question.title}
          subheader={
            <>
              <Typography
                variant="subtitle1"
                color="text.primary"
                sx={{ display: 'block' }}
              >
                {question.description}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 0.5 }}
              >
                <Chip
                  label={typeLabel}
                  color={(colorKey ?? 'default') as any}
                  size="small"
                  sx={{ fontSize: '0.7rem' }}
                />
                {question.required && (
                  <Chip
                    label="Required"
                    color="error"
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                )}
              </Stack>
            </>
          }
          slotProps={{
            title: { variant: 'h6' },
            subheader: { variant: 'body2', color: 'text.secondary' },
            avatar: { sx: { alignSelf: 'flex-start' } },
          }}
          sx={{ px: 0 }}
          avatar={
            <Stack direction="column" spacing={0}>
              <IconButton
                size="small"
                onClick={onMoveUp}
                disabled={onMoveUp === undefined}
                title="Move up"
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={onMoveDown}
                disabled={onMoveDown === undefined}
                title="Move down"
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
          action={
            <Stack direction="row" spacing={0}>
              <IconButton
                size="small"
                onClick={() => onEdit(question)}
                title="Edit"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onDelete(question.id)}
                title="Delete"
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        />

        {question.imageUrl && (
          <Box
            component="img"
            src={question.imageUrl}
            alt={question.title}
            sx={{
              width: '100%',
              maxHeight: 160,
              objectFit: 'cover',
              borderRadius: 1,
              mb: 1,
              mt: 0.5,
            }}
          />
        )}

        <Box sx={{ my: 1 }}>{renderContent()}</Box>
      </CardContent>
    </Card>
  );
};
